/** Client-only header chrome theme hydration (DOM). Re-exports SSR helpers from `-shared`. */

export * from './header-chrome-theme-shared';

import {
  buildHeaderChromeThemeCss,
  normalizeHeaderChromeColor,
  type StorefrontHeaderChromeColorSettings,
} from './header-chrome-theme-shared';

const CHROME_SELECTOR = '[data-storefront-header-chrome-row="true"]';
const CHROME_THEME_STYLE_SELECTOR = 'style[data-storefront-header-chrome-theme="true"]';

type ChromeDatasetKey =
  | 'chromeBackground'
  | 'chromeHoverBackground'
  | 'searchPanelBackground'
  | 'searchPanelHoverBackground'
  | 'accountPanelBackground'
  | 'accountPanelHoverBackground'
  | 'cartDrawerBackground'
  | 'cartDrawerHoverBackground';

const DATASET_KEYS: Record<keyof StorefrontHeaderChromeColorSettings, ChromeDatasetKey> = {
  chromeBackgroundColor: 'chromeBackground',
  chromeHoverBackgroundColor: 'chromeHoverBackground',
  searchPanelBackgroundColor: 'searchPanelBackground',
  searchPanelHoverBackgroundColor: 'searchPanelHoverBackground',
  accountPanelBackgroundColor: 'accountPanelBackground',
  accountPanelHoverBackgroundColor: 'accountPanelHoverBackground',
  cartDrawerBackgroundColor: 'cartDrawerBackground',
  cartDrawerHoverBackgroundColor: 'cartDrawerHoverBackground',
};

const CSS_VARS: Record<keyof StorefrontHeaderChromeColorSettings, string> = {
  chromeBackgroundColor: '--storefront-header-chrome-bg',
  chromeHoverBackgroundColor: '--storefront-header-chrome-hover-bg',
  searchPanelBackgroundColor: '--storefront-search-panel-bg',
  searchPanelHoverBackgroundColor: '--storefront-search-panel-hover-bg',
  accountPanelBackgroundColor: '--storefront-account-panel-bg',
  accountPanelHoverBackgroundColor: '--storefront-account-panel-hover-bg',
  cartDrawerBackgroundColor: '--storefront-cart-drawer-bg',
  cartDrawerHoverBackgroundColor: '--storefront-cart-drawer-hover-bg',
};

function readColorFromChrome(
  chrome: HTMLElement,
  settingKey: keyof StorefrontHeaderChromeColorSettings,
): string {
  const datasetKey = DATASET_KEYS[settingKey];
  const cssVar = CSS_VARS[settingKey];
  const fromData = normalizeHeaderChromeColor(chrome.dataset[datasetKey]);
  if (fromData) return fromData;
  const fromVar = normalizeHeaderChromeColor(chrome.style.getPropertyValue(cssVar));
  if (fromVar) return fromVar;
  const styleAttr = chrome.getAttribute('style') ?? '';
  const match = styleAttr.match(new RegExp(`${cssVar.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:\\s*(#[0-9a-fA-F]{3,6})`, 'i'));
  return match?.[1] ? normalizeHeaderChromeColor(match[1]) : '';
}

export function readHeaderChromeColorsFromElement(
  chrome: HTMLElement,
): StorefrontHeaderChromeColorSettings {
  return {
    chromeBackgroundColor: readColorFromChrome(chrome, 'chromeBackgroundColor'),
    chromeHoverBackgroundColor: readColorFromChrome(chrome, 'chromeHoverBackgroundColor'),
    searchPanelBackgroundColor: readColorFromChrome(chrome, 'searchPanelBackgroundColor'),
    searchPanelHoverBackgroundColor: readColorFromChrome(chrome, 'searchPanelHoverBackgroundColor'),
    accountPanelBackgroundColor: readColorFromChrome(chrome, 'accountPanelBackgroundColor'),
    accountPanelHoverBackgroundColor: readColorFromChrome(chrome, 'accountPanelHoverBackgroundColor'),
    cartDrawerBackgroundColor: readColorFromChrome(chrome, 'cartDrawerBackgroundColor'),
    cartDrawerHoverBackgroundColor: readColorFromChrome(chrome, 'cartDrawerHoverBackgroundColor'),
  };
}

function syncChromeThemeStyleSheet(headerMount: HTMLElement, css: string): void {
  if (!css) {
    headerMount.querySelector(CHROME_THEME_STYLE_SELECTOR)?.remove();
    return;
  }

  let styleEl = headerMount.querySelector(CHROME_THEME_STYLE_SELECTOR);
  if (styleEl instanceof HTMLStyleElement) {
    styleEl.textContent = css;
    return;
  }
  styleEl = document.createElement('style');
  styleEl.dataset.storefrontHeaderChromeTheme = 'true';
  styleEl.textContent = css;
  headerMount.insertBefore(styleEl, headerMount.firstChild);
}

function applyChromeColorDataset(
  chrome: HTMLElement,
  settingKey: keyof StorefrontHeaderChromeColorSettings,
  color: string,
): void {
  const datasetKey = DATASET_KEYS[settingKey];
  const cssVar = CSS_VARS[settingKey];
  if (color) {
    chrome.dataset[datasetKey] = color;
    chrome.style.setProperty(cssVar, color);
  } else {
    chrome.style.removeProperty(cssVar);
    delete chrome.dataset[datasetKey];
  }
}

export function applyHeaderChromeThemeToMount(
  headerMount: HTMLElement,
  settings: StorefrontHeaderChromeColorSettings = {},
): void {
  const chrome = headerMount.querySelector(CHROME_SELECTOR);
  if (!(chrome instanceof HTMLElement)) {
    return;
  }

  const incoming = settings;
  const existing = readHeaderChromeColorsFromElement(chrome);
  const resolved: StorefrontHeaderChromeColorSettings = {
    chromeBackgroundColor:
      normalizeHeaderChromeColor(incoming.chromeBackgroundColor) || existing.chromeBackgroundColor,
    chromeHoverBackgroundColor:
      normalizeHeaderChromeColor(incoming.chromeHoverBackgroundColor)
      || existing.chromeHoverBackgroundColor,
    searchPanelBackgroundColor:
      normalizeHeaderChromeColor(incoming.searchPanelBackgroundColor)
      || existing.searchPanelBackgroundColor,
    searchPanelHoverBackgroundColor:
      normalizeHeaderChromeColor(incoming.searchPanelHoverBackgroundColor)
      || existing.searchPanelHoverBackgroundColor,
    accountPanelBackgroundColor:
      normalizeHeaderChromeColor(incoming.accountPanelBackgroundColor)
      || existing.accountPanelBackgroundColor,
    accountPanelHoverBackgroundColor:
      normalizeHeaderChromeColor(incoming.accountPanelHoverBackgroundColor)
      || existing.accountPanelHoverBackgroundColor,
    cartDrawerBackgroundColor:
      normalizeHeaderChromeColor(incoming.cartDrawerBackgroundColor)
      || existing.cartDrawerBackgroundColor,
    cartDrawerHoverBackgroundColor:
      normalizeHeaderChromeColor(incoming.cartDrawerHoverBackgroundColor)
      || existing.cartDrawerHoverBackgroundColor,
  };

  const hasAnyColor = Object.values(resolved).some((color) => Boolean(color));
  if (!hasAnyColor) {
    syncChromeThemeStyleSheet(headerMount, '');
    for (const key of Object.keys(CSS_VARS) as Array<keyof StorefrontHeaderChromeColorSettings>) {
      chrome.style.removeProperty(CSS_VARS[key]);
      delete chrome.dataset[DATASET_KEYS[key]];
    }
    chrome.style.removeProperty('background-color');
    return;
  }

  for (const key of Object.keys(CSS_VARS) as Array<keyof StorefrontHeaderChromeColorSettings>) {
    const color = normalizeHeaderChromeColor(resolved[key]) ?? '';
    if (key === 'chromeBackgroundColor' && color) {
      chrome.style.setProperty('background-color', color, 'important');
    }
    applyChromeColorDataset(chrome, key, color);
  }

  syncChromeThemeStyleSheet(headerMount, buildHeaderChromeThemeCss(resolved));
}
